import React, { useState, useEffect } from "react";
import {
    Breadcrumb,
    Checkbox,
    ControlLabel,
    FormControl,
    FormGroup,
    Row,
    Col,
    Panel,
    Button,
    Modal,
    ButtonGroup,
    Glyphicon,
    ListGroupItem,
} from "react-bootstrap";
import cloneDeep from "lodash.clonedeep";
import PanelBody from "react-bootstrap/lib/PanelBody";
import PanelHeading from "react-bootstrap/lib/PanelHeading";
import PanelTitle from "react-bootstrap/lib/PanelTitle";
import Select from "react-select";
import { LinkContainer } from "react-router-bootstrap";
import { useEventContext, useUserContext } from "../../../../context";
import locale from "../../../../locale";
import FormControlLanguage from "../../../UI/FormControlLanguage";
import HelpHintButton from "components/HelpHintButton";
import FieldsPanel from "./FieldsPanel";
import { addEventModuleAuthField, removeEventModuleAuthField, updateEventModuleAuth } from "../../../../../api/cp/eventModuleAuth";
import { authTypesOptions as _authTypesOptions, participationTypesOptions, externalAuthServices } from "./lib";
import CodesEditModal from "./CodesEditModal";
import { getClientFields } from "../../../../../api/cp/clientRegistrationFields";
import DeleteConfirmOverlay from "../../../UI/DeleteConfirmOverlay";
import WebHookEditModal from "./WebHookEditModal";
import { removeWebHook, addWebHook } from "../../../../../api/cp/webHook";

const defaultDisclaimers = ["inn_disclaimer", "user_agreement_disclaimer"];

const hasPersonalData = (authValue, registrationFields) =>
    authValue === 'phone' || authValue === 'email' || registrationFields.filter(field => !defaultDisclaimers.includes(field.field.name)).length;

const AuthorizationSettings = ({ }) => {
    const { userIsAdmin } = useUserContext();
    const { event, setEvent } = useEventContext();
    const [moduleAuth, setModuleAuth] = useState(event.moduleAuth || {});
    const [authFields, setAuthFields] = useState([]);
    const [authFieldsFetched, setAuthFieldsFetched] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [isShowListCodes, setIsShowListCodes] = useState(false);
    const [editWebHook, setEditWebHook] = useState(null);

    const authTypesOptions = _authTypesOptions.filter(({ value }) => value !== 'phone' || event.moduleBranding?.allowSMSNotifications);

    const webHooks = moduleAuth.webHooks?.length ? moduleAuth.webHooks : [];

    const getAuthFields = async () => {
        try {
            const fields = await getClientFields();
            setAuthFields(fields || []);
        } catch (e) {
            console.error(e)
        } finally {
            setAuthFieldsFetched(true)
        }
    }

    const onSave = async (data) => {
        if (!event.client?.ssoSettings?.idpEntityId && moduleAuth.sso) {
            data.sso = false;
        }
        setModuleAuth(moduleAuth => ({ ...moduleAuth, ...data }));
        setSaving(true);
        try {
            const moduleAuth = await updateEventModuleAuth(event.eventKey, data);
            setModuleAuth(moduleAuth || {});
            setEvent(event => ({ ...event, moduleAuth }))
        } catch (e) {
            console.error(e)
        } finally {
            setSaving(false)
        }
    }

    const handleAuthType = async (authValue) => {
        const data = {};
        authTypesOptions
            .filter(({ value }) => value)
            .forEach(({ value }) => {
                data[value] = authValue === value;
            })
        await onSave(data);

        if (authValue === 'phone' && !event.registrationFields.find(({ field }) => field.name === 'phone')) {
            await handleAddField(authFields.find(({ name }) => name === 'phone'), true);
        }

        if (hasPersonalData(authValue, event.registrationFields)) {
            handleAddDisclaimers();
        }
    }

    const handleRequireToggle = (checked) => {
        const data = { require: checked, registrationConfirmEmail: checked };
        if (!checked) {
            ['allowLoginWithToken', 'accessPreModeration', 'accessAllowedConfirmEmail', 'accessOfflineByQr'].forEach(key => data[key] = false);
        } else {
            data['anonymous'] = false;
        }
        onSave(data);
    }

    const handleSaveField = async (id, data = {}) => {
        setSaving(true);
        try {
            const registrationFields = await addEventModuleAuthField(event.eventKey, id, data);
            setEvent(event => ({ ...event, registrationFields }));
        } catch (e) {
            console.error(e)
        } finally {
            setSaving(false);
        }
    }

    const handleAddField = async (field, required) => {
        if (!field) return;

        const data = {
            order: event.registrationFields.reduce((order, field) => Math.max(order, field.order), 0) + 1,
            required,
        };
        await handleSaveField(field.id, data);
    }

    const handleDeleteField = async (id) => {
        setSaving(true);
        const _registrationFields = cloneDeep(event.registrationFields);
        setEvent(event => ({ ...event, registrationFields: _registrationFields.filter(field => field.fieldId !== id) }));

        try {
            const registrationFields = await removeEventModuleAuthField(event.eventKey, id);
            setEvent(event => ({ ...event, registrationFields }));
        } catch (e) {
            console.error(e);
            setEvent(event => ({ ...event, registrationFields: _registrationFields }));
        } finally {
            setSaving(false);
        }
    };

    const handleAddDisclaimers = () => {
        defaultDisclaimers.forEach(async (disclaimer) => {
            const field = event.registrationFields.find(field => field.field.name === disclaimer);
            if (!field) {
                await handleAddField(authFields.find(({ name }) => name === disclaimer), true)
            } else if (!field.required) {
                handleSaveField(field.field.id, { required: true })
            }
        });
    }

    const setProperty = (key, value) => {
        setModuleAuth(moduleAuth => ({ ...moduleAuth, [key]: value }))
    }

    const handleEditWebHook = (id, key, value) => {
        let _editWebHook = cloneDeep(editWebHook);
        _editWebHook = {
            ..._editWebHook,
            [key]: value,
        }

        setProperty('webHooks', webHooks.map((wh) => wh.id === id ? _editWebHook : wh));
        setEditWebHook(_editWebHook);
    }

    const handleDeleteWebHook = async (id) => {
        setSaving(true);
        try {
            const webHooks = await removeWebHook(event.eventKey, id);
            setProperty('webHooks', webHooks || []);
            setEditWebHook(null);
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const handleAddWebHook = async () => {
        setSaving(true);
        try {
            const webHooks = await addWebHook(event.eventKey);
            setProperty('webHooks', webHooks || []);
            setEditWebHook(null);
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        getAuthFields();
    }, []);

    if (!authFieldsFetched) return null;

    return (
        <section>
            <Breadcrumb>
                <LinkContainer to={`/event/${event.eventPassword}`}>
                    <Breadcrumb.Item>{locale('mainMenu.eventSettings')}</Breadcrumb.Item>
                </LinkContainer>
                <Breadcrumb.Item active>{locale('mainMenu.eventSettings.authorization')}</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ display: 'flex', gap: '2rem' }}>

                <Panel style={{ flex: 1 }}>
                    <PanelHeading>
                        <PanelTitle>Общие настройки</PanelTitle>
                    </PanelHeading>
                    <PanelBody>
                        <FormGroup>
                            <Checkbox
                                checked={moduleAuth.require}
                                onChange={(e) => handleRequireToggle(e.target.checked)}
                            >Обязательная регистрация</Checkbox>
                        </FormGroup>
                        <FormGroup style={{ zIndex: 101, position: 'relative' }}>
                            <ControlLabel>Тип авторизации</ControlLabel>
                            <Select
                                value={authTypesOptions.find(({ value }) => !!moduleAuth[value]) || authTypesOptions[0]}
                                onChange={({ value }) => handleAuthType(value)}
                                options={authTypesOptions}
                                isDisabled={isSaving}
                            />
                        </FormGroup>
                        {moduleAuth.phone && userIsAdmin && (
                            <FormGroup>
                                <Button onClick={() => setIsShowListCodes(true)}>{locale('settings.allowedPhoneCountryCodes.buttonOpen')}</Button>
                            </FormGroup>
                        )}
                        {moduleAuth.guest && <FormGroup style={{ display: 'flex', gap: '2rem' }}>
                            <div style={{ flex: 1 }}>
                                <ControlLabel>Название поля запроса имени [Ru]</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={moduleAuth.guestNicknamePlaceholderRu}
                                    onChange={(e) => setProperty('guestNicknamePlaceholderRu', e.target.value)}
                                    onBlur={() => onSave({ guestNicknamePlaceholderRu: moduleAuth.guestNicknamePlaceholderRu })}
                                    disabled={isSaving}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <ControlLabel>Название поля запроса имени [En]</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={moduleAuth.guestNicknamePlaceholderEn}
                                    onChange={(e) => setProperty('guestNicknamePlaceholderEn', e.target.value)}
                                    onBlur={() => onSave({ guestNicknamePlaceholderEn: moduleAuth.guestNicknamePlaceholderEn })}
                                    disabled={isSaving}
                                />
                            </div>
                        </FormGroup>}
                        <FormGroup>
                            <ControlLabel>Формат имени</ControlLabel>
                            <FormControl
                                type="text"
                                value={moduleAuth.nickname}
                                placeholder="{surname} {name} {patronymic}"
                                onChange={(e) => setProperty('nickname', e.target.value)}
                                onBlur={() => onSave({ nickname: moduleAuth.nickname })}
                                disabled={isSaving}
                            />
                        </FormGroup>
                        <FormGroup style={{ zIndex: 100, position: 'relative' }}>
                            <ControlLabel>Разрешить регистрацию в</ControlLabel>
                            <Select
                                value={participationTypesOptions.find(({ value }) => moduleAuth.registrationType === value) || participationTypesOptions[0]}
                                onChange={(val) => onSave({ registrationType: val.value })}
                                options={participationTypesOptions}
                                isDisabled={isSaving}
                            />
                        </FormGroup>
                        <FormGroup>
                            <ControlLabel>Изменить нейминг для формы регистрации</ControlLabel>
                            <Row>
                                <Col xs={6}>
                                    <ControlLabel style={{ fontWeight: '500' }}>Онлайн</ControlLabel>
                                    <FormControlLanguage
                                        type="text"
                                        values={{
                                            ru: moduleAuth.participationFormatLabels[0].labelRu || '',
                                            en: moduleAuth.participationFormatLabels[0].labelEn || '',
                                        }}
                                        onChange={{
                                            ru: (e) => setProperty('participationFormatLabels', [{ value: 'online', labelRu: e.target.value, labelEn: moduleAuth.participationFormatLabels[0].labelEn || '' }, moduleAuth.participationFormatLabels[1]]),
                                            en: (e) => setProperty('participationFormatLabels', [{ value: 'online', labelRu: moduleAuth.participationFormatLabels[0].labelRu || '', labelEn: e.target.value }, moduleAuth.participationFormatLabels[1]]),
                                        }}
                                        onBlur={() => onSave({ participationFormatLabels: moduleAuth.participationFormatLabels })}
                                        disabled={isSaving}
                                    />
                                </Col>
                                <Col xs={6}>
                                    <ControlLabel style={{ fontWeight: '500' }}>Оффлайн</ControlLabel>
                                    <FormControlLanguage
                                        type="text"
                                        values={{
                                            ru: moduleAuth.participationFormatLabels[1].labelRu || '',
                                            en: moduleAuth.participationFormatLabels[1].labelEn || '',
                                        }}
                                        onChange={{
                                            ru: (e) => setProperty('participationFormatLabels', [moduleAuth.participationFormatLabels[0], { value: 'offline', labelRu: e.target.value, labelEn: moduleAuth.participationFormatLabels[1].labelEn || '' }]),
                                            en: (e) => setProperty('participationFormatLabels', [moduleAuth.participationFormatLabels[0], { value: 'offline', labelRu: moduleAuth.participationFormatLabels[1].labelRu || '', labelEn: e.target.value }]),
                                        }}
                                        onBlur={() => onSave({ participationFormatLabels: moduleAuth.participationFormatLabels })}
                                        disabled={isSaving}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                        <hr />
                        {moduleAuth.require ?
                            <>
                                <FormGroup>
                                    <Checkbox
                                        checked={moduleAuth.allowLoginWithToken}
                                        onChange={(e) => onSave({ allowLoginWithToken: e.target.checked })}
                                        disabled={isSaving}
                                    >Ссылка на платформу содержит токен</Checkbox>
                                </FormGroup>
                                <FormGroup>
                                    <Checkbox
                                        checked={moduleAuth.accessPreModeration}
                                        onChange={(e) => onSave({ accessPreModeration: e.target.checked, accessAllowedConfirmEmail: e.target.checked })}
                                        disabled={isSaving}
                                    >Премодерация доступа</Checkbox>
                                </FormGroup>
                                {moduleAuth.accessPreModeration &&
                                    <FormGroup>
                                        <Checkbox
                                            checked={moduleAuth.accessAllowedConfirmEmail}
                                            onChange={(e) => onSave({ accessAllowedConfirmEmail: e.target.checked })}
                                            disabled={isSaving}
                                        >Письмо о разрешении просмотра</Checkbox>
                                    </FormGroup>
                                }
                                <FormGroup>
                                    <Checkbox
                                        checked={moduleAuth.accessOfflineByQr}
                                        onChange={(e) => onSave({ accessOfflineByQr: e.target.checked })}
                                        disabled={isSaving}
                                    >
                                        Доступ на офлайн мероприятие по QR коду
                                        <HelpHintButton>
                                            <p>1. При регистрации каждому участнику будет создан уникальный QR код с его регистрационными данными</p>
                                            <p>2. В письмах включится дополнительный  блок "QR код для доступа на офлайн мероприятие"</p>
                                            <p>3. При отрправке письма конкретному пользователю с включенным блоком в него будет будет подставляться  созданный уникальный QR код, предназначенный</p>
                                        </HelpHintButton>
                                    </Checkbox>
                                </FormGroup>
                            </> :
                            <FormGroup>
                                <Checkbox
                                    checked={moduleAuth.anonymous}
                                    onChange={(e) => onSave({ anonymous: e.target.checked })}
                                    disabled={isSaving}
                                >Разрешить входить анонимно</Checkbox>
                            </FormGroup>
                        }
                    </PanelBody>
                </Panel>
                <Panel style={{ flex: 1 }}>
                    <PanelHeading>
                        <PanelTitle>Поля регистрации</PanelTitle>
                    </PanelHeading>
                    <PanelBody>
                        <FieldsPanel
                            moduleAuth={moduleAuth}
                            handleAddDisclaimers={handleAddDisclaimers}
                            defaultDisclaimers={defaultDisclaimers}
                            authFields={authFields}
                            setAuthFields={setAuthFields}
                            handleAddField={handleAddField}
                            handleSaveField={handleSaveField}
                            handleDeleteField={handleDeleteField}
                            registrationFields={event.registrationFields || []}
                            setRegistrationFields={registrationFields => setEvent(event => ({ ...event, registrationFields }))}
                        />
                    </PanelBody>
                </Panel>
                <Panel style={{ flex: 1 }}>
                    <PanelHeading>
                        <PanelTitle>Дополнительно</PanelTitle>
                    </PanelHeading>
                    <PanelBody>
                        <FormGroup>
                            <Checkbox
                                checked={moduleAuth.registrationConfirmEmail}
                                onChange={(e) => onSave({ registrationConfirmEmail: e.target.checked })}
                                disabled={isSaving}
                            >
                                Письмо о регистрации
                                <HelpHintButton>
                                    <p>Отправлять автоматическое письмо о регистрации после заполнения формы</p>
                                </HelpHintButton>
                            </Checkbox>
                        </FormGroup>
                        <FormGroup>
                            <Checkbox
                                checked={moduleAuth.accessByEmails}
                                onChange={(e) => onSave({ accessByEmails: e.target.checked })}
                                disabled={isSaving}
                            >Доступ по списку email</Checkbox>
                        </FormGroup>
                        <FormGroup>
                            <Checkbox
                                checked={moduleAuth.corporateMail}
                                onChange={(e) => onSave({ corporateMail: e.target.checked })}
                                disabled={isSaving}
                            >Выводить сообщение о необходимости использовать корпоративную почту</Checkbox>
                        </FormGroup>
                        <hr />
                        <FormGroup>
                            <ControlLabel>Допустимые домены почты</ControlLabel>
                            <FormControl
                                type="text"
                                value={moduleAuth.allowedEmailDomain}
                                placeholder="mail.ru yandex.ru gmail.com"
                                onChange={(e) => setProperty('allowedEmailDomain', e.target.value)}
                                onBlur={() => onSave({ allowedEmailDomain: moduleAuth.allowedEmailDomain })}
                                disabled={isSaving}
                            />
                        </FormGroup>
                        {!event.private &&
                            <FormGroup>
                                <ControlLabel>Внешняя авторизация</ControlLabel>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                    {externalAuthServices
                                        .filter(({ id }) => id !== 'sso' || !!event.client?.ssoSettings?.idpEntityId)
                                        .map(({ id, label }) => <Checkbox
                                            key={id}
                                            checked={moduleAuth[id]}
                                            onChange={(e) => onSave({ [id]: e.target.checked })}
                                            style={{ margin: 0, width: '30%' }}
                                            disabled={isSaving}
                                        >{label}</Checkbox>)}
                                </div>
                            </FormGroup>
                        }
                        <hr />
                        <FormGroup>
                            <Checkbox
                                checked={moduleAuth.useWebHook}
                                onChange={(e) => onSave({ useWebHook: e.target.checked })}
                                disabled={isSaving}
                            >
                                Передавать web hooks в сервис
                                <HelpHintButton>
                                    <div style={{ textAlign: 'left' }}>
                                        <p>Будет отправлен запрос PUT на указанный url в заголовке x-api-key value будет передан secretkey</p>
                                        <p>пример запроса:</p>
                                        <p>
                                            {'[\n{\n"id:"id1,\n"lastname":"lastname1",\n"firstname":"firstname1"\n},\n{\n"id":"id2",\n"lastname":"lastname2",\n"firstname":"firstname2"\n}\n]'}
                                        </p>
                                        <p>Веб хук ожидает в ответ при удачной отправке:</p>
                                        <p>{'"data": [{"id": ***   }]'}</p>
                                        <p>Коды ошибок которые могут быть обработаны:</p>
                                        <p>Authentication is possible but has failed or not yet been provided. 401 error</p>
                                        <p>The path you requested does not exist 404 error</p>
                                        <p>{'{"error": "UnsupportedFormat"}'}</p>
                                    </div>
                                </HelpHintButton>
                            </Checkbox>
                        </FormGroup>
                        <FormGroup>
                            <ControlLabel>
                                Сервисы web hooks
                            </ControlLabel>
                            {webHooks.map((webHook) => (
                                <ListGroupItem
                                    key={webHook.id}
                                    style={{
                                        padding: '4px 4px 4px 12px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        height: '44px',
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', flex: 1 }}>
                                        {webHook.url || locale('settings.webHooks.title')}
                                    </span>
                                    <ButtonGroup>
                                        <Button onClick={() => setEditWebHook(webHook)}>
                                            <Glyphicon glyph="edit" />
                                        </Button>
                                        <DeleteConfirmOverlay
                                            title={locale('settings.webHooks.remove')}
                                            buttonText="Удалить"
                                            onConfirm={() => handleDeleteWebHook(webHook.id)}
                                        >
                                            <Button bsStyle="danger"><Glyphicon glyph="trash" /></Button>
                                        </DeleteConfirmOverlay>
                                    </ButtonGroup>
                                </ListGroupItem>
                            ))}
                            <Button
                                style={{ width: "100%", marginTop: 20 }}
                                onClick={handleAddWebHook}
                            >
                                {locale('settings.webHooks.add')}
                            </Button>
                        </FormGroup>
                        <hr />
                        <FormGroup>
                            <ControlLabel>
                                Дополнительное описание
                                <HelpHintButton>
                                    Свой текст над формой запроса пароля
                                </HelpHintButton>
                            </ControlLabel>
                            <FormControlLanguage
                                type="textarea"
                                rows={4}
                                values={{
                                    ru: moduleAuth.customDescriptionRu || '',
                                    en: moduleAuth.customDescriptionEn || '',
                                }}
                                onChange={{
                                    ru: (e) => setProperty('customDescriptionRu', e.target.value),
                                    en: (e) => setProperty('customDescriptionEn', e.target.value),
                                }}
                                onBlur={() => onSave({ customDescriptionRu: moduleAuth.customDescriptionRu, customDescriptionEn: moduleAuth.customDescriptionEn })}
                                disabled={isSaving}
                            />
                        </FormGroup>
                        {/* <FormGroup>
                            <ControlLabel>
                                Дополнительное соглашение
                                <HelpHintButton>
                                    Для того, чтобы добавить ссылку, используйте подсказу под полем ввода
                                </HelpHintButton>
                            </ControlLabel>
                            <FormControlLanguage
                                type="textarea"
                                rows={4}
                                values={{
                                    ru: moduleAuth.customRulesRu || '',
                                    en: moduleAuth.customRulesEn || '',
                                }}
                                onChange={{
                                    ru: (e) => setProperty('customRulesRu', e.target.value),
                                    en: (e) => setProperty('customRulesEn', e.target.value),
                                }}
                                onBlur={() => onSave({ customRulesRu: moduleAuth.customRulesRu, customRulesEn: moduleAuth.customRulesEn })}
                                disabled={isSaving}
                            />
                            <span><i>{'<a href="адрес_страницы" target="_blank">название_ссылки</a>'}</i></span>
                        </FormGroup> */}
                    </PanelBody>
                </Panel>
            </div>
            <Modal
                show={isShowListCodes}
                onHide={() => setIsShowListCodes(false)}
            >
                <CodesEditModal
                    locale={locale}
                    allowedPhoneCountryCodes={moduleAuth?.allowedPhoneCountryCodes}
                    handleSave={onSave}
                    isSaving={isSaving}
                />
            </Modal>
            <Modal
                show={!!editWebHook}
                onHide={() => setEditWebHook(false)}
            >
                <WebHookEditModal
                    locale={locale}
                    webHook={editWebHook}
                    handleEditWebHook={handleEditWebHook}
                    eventKey={event.eventKey}
                />
            </Modal>
        </section>
    );
};

export default AuthorizationSettings;
